import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { bookingAPI } from "../../services/api";
import { useAuth } from "../../context/AuthContext";
import { useSEO } from "../../hooks";
import { format } from "date-fns";
import resolveImage from "../../utils/imageUrl";
import toast from "react-hot-toast";
import {
  FiArrowLeft,
  FiMapPin,
  FiCalendar,
  FiClock,
  FiStar,
} from "react-icons/fi";
import "../dashboard/Dashboard.css";
import "../dashboard/Modal.css";

const BookingDetail = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);

  useSEO({
    title: booking
      ? `Booking #${booking._id?.slice(-6).toUpperCase()} — ${booking.service?.name || "Service"}`
      : "Booking Details",
    description:
      "View your Expert At Door booking details, status, and manage your appointment.",
    canonical: `/booking/${id}`,
    noindex: true,
  });

  const fetchBooking = async () => {
    try {
      const { data } = await bookingAPI.getById(id);
      setBooking(data.data);
    } catch {
      toast.error("Booking not found");
      navigate(-1);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBooking();
  }, [id]);

  if (loading)
    return (
      <div className="booking-detail-page">
        <div className="container">
          <div
            className="skeleton"
            style={{ height: 400, borderRadius: "var(--radius-lg)" }}
          />
        </div>
      </div>
    );

  if (!booking) return null;

  const isCustomer = user?.role === "customer";
  const canReview =
    isCustomer && booking.status === "completed" && !booking.isReviewed;

  return (
    <div className="booking-detail-page">
      <div className="container">
        {/* Back + title */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "var(--space-4)",
            marginBottom: "var(--space-6)",
          }}
        >
          <button className="btn btn-ghost btn-sm" onClick={() => navigate(-1)}>
            <FiArrowLeft size={15} /> Back
          </button>
          <div>
            <h2 style={{ fontSize: "1.3rem" }}>
              Booking #{booking.bookingNumber}
            </h2>
            <span
              className={`badge badge-${booking.status}`}
              style={{ marginTop: 4 }}
            >
              {booking.status.replace("_", " ")}
            </span>
          </div>
        </div>

        <div className="booking-detail-grid">
          {/* Left col */}
          <div>
            {/* Service info */}
            <div className="detail-card">
              <p className="detail-card-title">Service Details</p>
              <div
                style={{
                  display: "flex",
                  gap: "var(--space-4)",
                  marginBottom: "var(--space-5)",
                  alignItems: "flex-start",
                }}
              >
                <div
                  style={{
                    width: 72,
                    height: 72,
                    borderRadius: "var(--radius-md)",
                    overflow: "hidden",
                    background: "var(--brand-surface-2)",
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "1.8rem",
                  }}
                >
                  {booking.serviceSnapshot?.image ? (
                    <img
                      src={booking.serviceSnapshot.image}
                      alt={booking.serviceSnapshot.name}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  ) : (
                    "🔧"
                  )}
                </div>
                <div>
                  <h3 style={{ fontSize: "1.1rem", marginBottom: 4 }}>
                    {booking.serviceSnapshot?.name}
                  </h3>
                  <p style={{ fontSize: "0.85rem", margin: 0 }}>
                    {booking.serviceSnapshot?.categoryName}
                  </p>
                </div>
              </div>
              {[
                {
                  key: "Scheduled date",
                  val: format(
                    new Date(booking.scheduledDate),
                    "EEEE, dd MMMM yyyy",
                  ),
                },
                { key: "Time slot", val: booking.scheduledTimeSlot },
                {
                  key: "Service address",
                  val: `${booking.address?.line1}, ${booking.address?.city} - ${booking.address?.pincode}`,
                },
                ...(booking.notes
                  ? [{ key: "Notes", val: booking.notes }]
                  : []),
              ].map(({ key, val }) => (
                <div key={key} className="detail-row">
                  <span className="detail-key">{key}</span>
                  <span className="detail-val">{val}</span>
                </div>
              ))}
            </div>

            {/* Assigned expert */}
            {booking.technicianId && (
              <div className="detail-card">
                <p className="detail-card-title">Your Expert</p>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "var(--space-4)",
                  }}
                >
                  <div className="tech-avatar">
                    {booking.technicianId.profileImage ? (
                      <img
                        src={resolveImage(booking.technicianId.profileImage)}
                        alt={booking.technicianId.name}
                      />
                    ) : (
                      <span>{booking.technicianId.name?.[0]}</span>
                    )}
                  </div>
                  <div>
                    <p className="tech-name">{booking.technicianId.name}</p>
                    <p className="tech-meta">{booking.technicianId.phone}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Status timeline */}
            <div className="detail-card">
              <p className="detail-card-title">Status History</p>
              <div className="status-timeline">
                {booking.statusHistory?.map((h, i) => (
                  <div key={i} className="timeline-item">
                    <div
                      className={`timeline-dot ${i === booking.statusHistory.length - 1 ? "active" : ""}`}
                    >
                      ✓
                    </div>
                    <div className="timeline-content">
                      <p className="timeline-status">
                        {h.status.replace("_", " ")}
                      </p>
                      {h.note && <p className="timeline-note">{h.note}</p>}
                      <p className="timeline-date">
                        {format(new Date(h.changedAt), "dd MMM yyyy, h:mm a")}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right col */}
          <div>
            {/* Payment summary */}
            <div className="detail-card">
              <p className="detail-card-title">Payment Summary</p>
              {[
                { key: "Base amount", val: `₹${booking.baseAmount}` },
                ...(booking.additionalCharges
                  ? [
                      {
                        key: "Additional charges",
                        val: `₹${booking.additionalCharges}`,
                      },
                    ]
                  : []),
                ...(booking.discount
                  ? [{ key: "Discount", val: `-₹${booking.discount}` }]
                  : []),
              ].map(({ key, val }) => (
                <div key={key} className="detail-row">
                  <span className="detail-key">{key}</span>
                  <span className="detail-val">{val}</span>
                </div>
              ))}
              <div
                className="detail-row"
                style={{
                  borderTop: "2px solid var(--brand-border)",
                  marginTop: 4,
                }}
              >
                <span style={{ fontWeight: 700, color: "var(--brand-ink)" }}>
                  Total
                </span>
                <span
                  style={{
                    fontFamily: "var(--font-display)",
                    fontWeight: 800,
                    fontSize: "1.1rem",
                  }}
                >
                  ₹{booking.totalAmount}
                </span>
              </div>
              <div className="pay-after-detail-notice">
                <span>💳</span>
                <p>
                  Pay in cash or UPI directly to the expert{" "}
                  <strong>after</strong> the service is completed to your
                  satisfaction. No upfront payment needed.
                </p>
              </div>
            </div>

            {/* Review CTA */}
            {canReview && (
              <div className="detail-card" style={{ textAlign: "center" }}>
                <p className="detail-card-title">Rate this Service</p>
                <FiStar
                  size={32}
                  color="var(--brand-accent)"
                  style={{ margin: "0 auto var(--space-3)" }}
                />
                <p
                  style={{
                    fontSize: "0.875rem",
                    marginBottom: "var(--space-4)",
                  }}
                >
                  How was your experience? Your feedback helps our experts
                  improve.
                </p>
                <Link
                  to={`/review/${booking._id}`}
                  className="btn btn-primary btn-full"
                >
                  Write a Review
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingDetail;
